/**
 * DocumentComparisonResults - Displays completed comparison results.
 *
 * Shows summary statistics, download buttons for result zips, and bulk
 * actions to mark all document entity changes as transferred or skipped.
 */

import {
  ArrowDownTrayIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ForwardIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';

import { cn } from '@/lib/utils';
import type { DocumentComparisonSchema } from '@/services/api/documentComparisonService';
import {
  useLazyGetDownloadMissingExternallyQuery,
  useLazyGetDownloadMissingInternallyQuery,
} from '@/services/api/documentComparisonService';
import { useEntityChangesBulkUpdateStatusMutation } from '@/services/api/generated/entityChangesService';

interface DocumentComparisonResultsProps {
  comparison: DocumentComparisonSchema;
  matterId: string;
  documentEntityChangeIds: string[];
}

export const DocumentComparisonResults: React.FC<
  DocumentComparisonResultsProps
> = ({ comparison, matterId, documentEntityChangeIds }) => {
  const [fetchMissingInternally] = useLazyGetDownloadMissingInternallyQuery();
  const [fetchMissingExternally] = useLazyGetDownloadMissingExternallyQuery();
  const [bulkUpdateStatus] = useEntityChangesBulkUpdateStatusMutation();
  const [bulkActionDone, setBulkActionDone] = useState<
    'transferred' | 'skipped' | null
  >(null);

  const matchedCount =
    comparison.total_external_files -
    comparison.missing_internally_count -
    comparison.missing_externally_count;

  const handleDownload = useCallback(
    async (direction: 'internally' | 'externally') => {
      const fetcher =
        direction === 'internally'
          ? fetchMissingInternally
          : fetchMissingExternally;
      const result = await fetcher({
        matterId,
        comparisonId: comparison.id,
      }).unwrap();
      if (result.download_url) {
        window.open(result.download_url, '_blank');
      }
    },
    [matterId, comparison.id, fetchMissingInternally, fetchMissingExternally],
  );

  const handleBulkAction = useCallback(
    async (action: 'transferred' | 'skipped') => {
      if (documentEntityChangeIds.length === 0) return;
      await bulkUpdateStatus({
        bulkEntityChangeStatusRequestSchema: {
          entity_change_ids: documentEntityChangeIds,
          action,
        },
      }).unwrap();
      setBulkActionDone(action);
    },
    [documentEntityChangeIds, bulkUpdateStatus],
  );

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-2">
        <StatCard
          label="External files"
          value={comparison.total_external_files}
          className="text-gray-600"
        />
        <StatCard
          label="Internal files"
          value={comparison.total_internal_files}
          className="text-gray-600"
        />
        <StatCard
          label="Matched"
          value={Math.max(0, matchedCount)}
          className="text-green-600"
          icon={<CheckCircleIcon className="h-3.5 w-3.5" />}
        />
        <StatCard
          label="Missing in Finch"
          value={comparison.missing_internally_count}
          className="text-amber-600"
          icon={<XCircleIcon className="h-3.5 w-3.5" />}
        />
      </div>

      {comparison.missing_externally_count > 0 && (
        <div className="rounded-md bg-blue-50/60 px-3 py-2 text-xs text-blue-700">
          <span className="font-medium">
            {comparison.missing_externally_count}
          </span>{' '}
          Finch {comparison.missing_externally_count === 1 ? 'file' : 'files'}{' '}
          not found in external CMS
        </div>
      )}

      {/* Download buttons */}
      <div className="space-y-1.5">
        {comparison.missing_internally_count > 0 && (
          <button
            type="button"
            onClick={() => handleDownload('internally')}
            className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:border-gray-300"
          >
            <ArrowDownTrayIcon className="h-4 w-4 text-indigo-500" />
            <span className="flex-1 text-left">
              Download missing in Finch
            </span>
            <span className="text-gray-400">
              {comparison.missing_internally_count} files
            </span>
          </button>
        )}
        {comparison.missing_externally_count > 0 && (
          <button
            type="button"
            onClick={() => handleDownload('externally')}
            className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:border-gray-300"
          >
            <ArrowDownTrayIcon className="h-4 w-4 text-violet-500" />
            <span className="flex-1 text-left">
              Download missing externally
            </span>
            <span className="text-gray-400">
              {comparison.missing_externally_count} files
            </span>
          </button>
        )}
      </div>

      {/* Bulk actions */}
      {documentEntityChangeIds.length > 0 && !bulkActionDone && (
        <div className="space-y-1.5 pt-1">
          <p className="text-xs text-gray-500">
            Bulk update {documentEntityChangeIds.length} document{' '}
            {documentEntityChangeIds.length === 1 ? 'change' : 'changes'}
          </p>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => handleBulkAction('transferred')}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100"
            >
              <ArrowRightIcon className="h-3.5 w-3.5" />
              Mark Transferred
            </button>
            <button
              type="button"
              onClick={() => handleBulkAction('skipped')}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-gray-100 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
            >
              <ForwardIcon className="h-3.5 w-3.5" />
              Mark Skipped
            </button>
          </div>
        </div>
      )}

      {bulkActionDone && (
        <div className="rounded-md bg-green-50 px-3 py-2 text-xs text-green-700 flex items-center gap-1.5">
          <CheckCircleIcon className="h-4 w-4" />
          All documents marked as {bulkActionDone}
        </div>
      )}
    </div>
  );
};

/* ── Stat card helper ──────────────────────────────────────────────────── */

const StatCard: React.FC<{
  label: string;
  value: number;
  className?: string;
  icon?: React.ReactNode;
}> = ({ label, value, className, icon }) => (
  <div className="rounded-md bg-gray-50 px-3 py-2">
    <div className={cn('flex items-center gap-1 text-lg font-semibold', className)}>
      {icon}
      {value}
    </div>
    <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">
      {label}
    </p>
  </div>
);
